import { useEffect, useMemo, useState } from 'react';
import { conversationTitle } from '../lib/chat-content';
import { emptyConversation, loadChatState, persistChatState } from '../lib/storage';
import type { ChatMessage, Conversation, StoredChatState } from '../types/chat';

export function useConversations() {
  const [state, setState] = useState<StoredChatState>(loadChatState);

  useEffect(() => {
    persistChatState(state);
  }, [state]);

  const active = useMemo(
    () => state.conversations.find((conversation) => conversation.id === state.activeId) ?? state.conversations[0],
    [state],
  );

  const updateConversation = (conversationId: string, update: (conversation: Conversation) => Conversation) => {
    setState((current) => ({
      ...current,
      conversations: current.conversations.map((conversation) =>
        conversation.id === conversationId ? update(conversation) : conversation,
      ),
    }));
  };

  const setMessages = (
    conversationId: string,
    messagesOrUpdate: ChatMessage[] | ((messages: ChatMessage[]) => ChatMessage[]),
  ) => {
    updateConversation(conversationId, (conversation) => {
      const messages = typeof messagesOrUpdate === 'function'
        ? messagesOrUpdate(conversation.messages)
        : messagesOrUpdate;
      const firstUser = messages.find((message) => message.role === 'user');
      return {
        ...conversation,
        messages,
        title: firstUser ? conversationTitle(firstUser) : 'Percakapan baru',
        updatedAt: Date.now(),
      };
    });
  };

  const createConversation = (): string => {
    const conversation = emptyConversation();
    setState((current) => ({
      ...current,
      activeId: conversation.id,
      conversations: [conversation, ...current.conversations],
    }));
    return conversation.id;
  };

  const resetConversation = (conversationId: string) => {
    setMessages(conversationId, []);
  };

  const selectConversation = (conversationId: string) => {
    setState((current) => ({ ...current, activeId: conversationId }));
  };

  return {
    conversations: state.conversations,
    active,
    activeId: state.activeId,
    setMessages,
    createConversation,
    resetConversation,
    selectConversation,
  };
}
